import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import useWindowSize from '../../hooks/useWindowSize';

const DateRangePicker = ({ startDate, endDate, onStartDateChange, onEndDateChange, onClose }) => {
  const { isMobile } = useWindowSize();
  /** vertical = mois empilés (mobile), horizontal = 2 mois côte à côte (desktop) */
  const orientation = isMobile ? 'vertical' : 'horizontal';
  const numberOfMonths = 2;

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingEnd, setSelectingEnd] = useState(false);

  const DAYS = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];
  const MONTHS = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const getFirstDayOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const generateDays = (monthDate) => {
    const days = [];
    const firstDay = getFirstDayOfMonth(monthDate);
    const daysInMonth = getDaysInMonth(monthDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, disabled: true });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
      const dateStr = formatDate(date);
      const isPast = date < today;
      const isStart = startDate === dateStr;
      const isEnd = endDate === dateStr;
      const isInRange = startDate && endDate && dateStr > startDate && dateStr < endDate;
      const isToday = date.toDateString() === today.toDateString();

      days.push({
        day,
        date: dateStr,
        disabled: isPast,
        isStart,
        isEnd,
        isInRange,
        isToday
      });
    }

    return days;
  };

  const formatDate = (date) => {
    // Utiliser le fuseau local pour éviter le décalage UTC (-1 jour selon timezone)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDayClick = (dateStr) => {
    if (!dateStr) return;

    if (!selectingEnd || !startDate) {
      onStartDateChange(dateStr);
      onEndDateChange('');
      setSelectingEnd(true);
    } else {
      if (dateStr < startDate) {
        onStartDateChange(dateStr);
        onEndDateChange('');
      } else {
        onEndDateChange(dateStr);
        setSelectingEnd(false);
      }
    }
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '---';
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return '---';
    return `${day} ${MONTHS[month - 1].slice(0, 3)}`;
  };

  const displayedMonths = Array.from({ length: numberOfMonths }, (_, i) =>
    new Date(currentMonth.getFullYear(), currentMonth.getMonth() + i, 1)
  );
  const canGoPrev = currentMonth > new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  const monthRangeNavLabel = () => {
    const first = displayedMonths[0];
    const last = displayedMonths[displayedMonths.length - 1];
    const y1 = first.getFullYear();
    const y2 = last.getFullYear();
    const m1 = MONTHS[first.getMonth()];
    const m2 = MONTHS[last.getMonth()];
    if (y1 === y2) return `${m1} – ${m2} ${y1}`;
    return `${m1} ${y1} – ${m2} ${y2}`;
  };

  const renderMonthGrid = (monthDate) => (
    <>
      {isMobile && (
        <h4 className="text-center text-base font-bold text-gray-900 mb-3">
          {MONTHS[monthDate.getMonth()]} {monthDate.getFullYear()}
        </h4>
      )}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAYS.map((day, i) => (
          <div key={`${monthDate.getTime()}-dow-${i}`} className="text-center text-xs font-bold text-gray-400 py-2">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {generateDays(monthDate).map((item, index) => (
          <button
            key={`${monthDate.getTime()}-${index}`}
            onClick={() => !item.disabled && handleDayClick(item.date)}
            disabled={item.disabled || !item.day}
            className={`
                  relative h-10 rounded-lg text-sm font-medium transition-all duration-150
                  ${!item.day ? 'invisible' : ''}
                  ${item.disabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-[#27489E]/10 text-gray-700'}
                  ${item.isToday && !item.isStart && !item.isEnd ? 'ring-2 ring-[#27489E]/50 ring-inset' : ''}
                  ${item.isStart ? 'bg-[#27489E] text-white hover:bg-[#1e3a7d] rounded-r-none' : ''}
                  ${item.isEnd ? 'bg-[#27489E] text-white hover:bg-[#1e3a7d] rounded-l-none' : ''}
                  ${item.isInRange ? 'bg-[#27489E]/20 text-[#27489E] rounded-none' : ''}
                  ${item.isStart && item.isEnd ? 'rounded-lg' : ''}
                `}
          >
            {item.day}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden w-full max-w-[700px] min-w-0">
      {/* Header */}
      <div className="bg-[#27489E] text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5" />
            <span className="font-semibold">Sélectionnez vos dates</span>
          </div>
          <button 
            onClick={onClose}
            className="text-white/80 hover:text-white transition text-xl font-light w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20"
          >
            ✕
          </button>
        </div>
        
        {/* Dates sélectionnées */}
        <div className="flex items-center gap-4 mt-3">
          <div className={`flex-1 p-3 rounded-xl transition ${!selectingEnd ? 'bg-white/20 ring-2 ring-white' : 'bg-white/10'}`}>
            <div className="text-[10px] uppercase tracking-wider text-blue-100">Départ</div>
            <div className="font-bold text-lg">{formatDisplayDate(startDate)}</div>
          </div>
          <div className="text-2xl text-white/50">→</div>
          <div className={`flex-1 p-3 rounded-xl transition ${selectingEnd && startDate ? 'bg-white/20 ring-2 ring-white' : 'bg-white/10'}`}>
            <div className="text-[10px] uppercase tracking-wider text-blue-100">Retour</div>
            <div className="font-bold text-lg">{formatDisplayDate(endDate)}</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-100 gap-2">
        <button
          type="button"
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          className={`shrink-0 p-2 rounded-full transition ${canGoPrev ? 'hover:bg-gray-100 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        {orientation === 'horizontal' ? (
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-1 min-w-0">
            {displayedMonths.map((m) => (
              <span key={m.getTime()} className="font-bold text-gray-800 whitespace-nowrap">
                {MONTHS[m.getMonth()]} {m.getFullYear()}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm font-semibold text-gray-800 text-center leading-tight min-w-0">
            {monthRangeNavLabel()}
          </span>
        )}
        <button
          type="button"
          onClick={goToNextMonth}
          className="shrink-0 p-2 rounded-full hover:bg-gray-100 text-gray-700 transition"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Calendriers : vertical sur mobile (scroll), horizontal sur desktop */}
      <div
        className={`
          calendrier-wrapper flex p-4 md:p-6 gap-6 min-h-0
          ${orientation === 'vertical' ? 'flex-col max-h-[min(62vh,560px)] overflow-y-auto overflow-x-hidden pb-24' : 'flex-row'}
        `}
      >
        {displayedMonths.map((m, idx) => (
          <React.Fragment key={m.getTime()}>
            {idx > 0 && (
              <div
                className={
                  orientation === 'vertical'
                    ? 'h-px w-full bg-gray-200 shrink-0'
                    : 'w-px bg-gray-200 self-stretch shrink-0'
                }
                aria-hidden
              />
            )}
            <div className="flex-1 min-w-0">{renderMonthGrid(m)}</div>
          </React.Fragment>
        ))}
      </div>

      {/* Footer */}
      <div
        className={`
          container-bouton-confirmer bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-3
          ${isMobile
            ? 'fixed bottom-0 left-0 right-0 z-[1000] px-4 py-[15px] shadow-[0_-2px_10px_rgba(0,0,0,0.1)]'
            : 'static px-6 py-4'
          }
        `}
      >
        <div className="text-sm text-gray-500">
          {startDate && endDate ? (
            <span className="text-green-600 font-medium">
              ✓ {Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24))} jours sélectionnés
            </span>
          ) : (
            <span>{selectingEnd ? 'Sélectionnez la date de retour' : 'Sélectionnez la date de départ'}</span>
          )}
        </div>
        <button
          onClick={onClose}
          disabled={!startDate || !endDate}
          className={`px-6 py-2.5 rounded-xl font-semibold transition ${
            startDate && endDate 
              ? 'bg-[#27489E] text-white hover:bg-[#1e3a7d] shadow-lg shadow-[#27489E]/30' 
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Confirmer
        </button>
      </div>
    </div>
  );
};

export default DateRangePicker;
